import type { LupeReply } from '@/lib/types/universe'

/** Base URL of Lupe's web API. */
export const LUPE_URL: string = (
  import.meta.env.VITE_LUPE_URL ?? 'https://lupe.loop3.ai'
).replace(/\/$/, '')

const UNAVAILABLE = 'Lupe is unavailable'

/** Authenticated JSON request to Lupe; any failure surfaces as an Error with a user-facing message. */
async function lupeFetch<T>(
  token: string,
  path: string,
  init?: RequestInit
): Promise<T> {
  let res: Response
  try {
    res = await fetch(`${LUPE_URL}${path}`, {
      ...init,
      headers: {
        ...init?.headers,
        Authorization: `Bearer ${token}`,
        ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      },
    })
  } catch {
    throw new Error(UNAVAILABLE)
  }
  const body = (await res.json().catch(() => ({}))) as { error?: string }
  if (!res.ok) throw new Error(body.error ?? UNAVAILABLE)
  return body as T
}

/** Sends one chat turn plus prior history to Lupe. */
export function askLupe(
  token: string,
  body: {
    message: string
    history: { role: 'user' | 'assistant'; text: string }[]
  }
): Promise<LupeReply> {
  return lupeFetch<LupeReply>(token, '/web/chat', {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export interface BuildingItem {
  title: string
  summary: string
  sources: ('kb' | 'linear' | 'meetings')[]
}

/** What Loop3 is building for a client, summarized by Lupe from the KB graph, Linear and meetings. */
export async function getClientBuilding(
  token: string,
  prefix: string
): Promise<BuildingItem[]> {
  const { items } = await lupeFetch<{ items: BuildingItem[] }>(
    token,
    `/web/clients/${encodeURIComponent(prefix)}/building`
  )
  return items
}

/** Asks Lupe to run the kb sync now; 'running' when a sync is already in progress. */
export async function requestKbRefresh(
  token: string
): Promise<'started' | 'running'> {
  const { status } = await lupeFetch<{ status: 'started' | 'running' }>(
    token,
    '/web/kb/sync',
    { method: 'POST' }
  )
  return status
}
